import { useEffect, useId, useState, type Dispatch, type ReactNode } from "react";
import type { Font, TemplateAsset, TemplateDefinition } from "@/api/client";
import { ToolbarButton } from "@/components/ui";
import type { DesignerAction, FieldPatch } from "@/features/templates/designer/definition";
import {
  FONT_STYLES,
  STYLE_LABELS,
  QR_BINDS,
  TEXT_BINDS,
  bindLabel,
  alignOf,
  fieldLabel,
  newField,
  type NewField,
} from "@/features/templates/designer/fields";
import { margins, textWidthMM, type Field, type Page } from "@/features/templates/designer/geometry";

const inputClass =
  "mt-1 block w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand disabled:bg-zinc-50 disabled:text-zinc-500";

/** The issues shown under one input; the input is marked invalid and described by them. */
function Issues({ id, issues }: { id: string; issues: string[] }) {
  if (issues.length === 0) return null;
  return (
    <span id={id} className="mt-1 block text-xs text-red-700">
      {issues.join(". ")}
    </span>
  );
}

const invalidProps = (id: string, issues: string[] = []) =>
  issues.length > 0 ? { "aria-invalid": true, "aria-describedby": id } : {};

/** Issues addressed to the panel itself rather than to one of its inputs. */
function PanelIssues({ issues }: { issues: string[] }) {
  return issues.length > 0 ? <p className="text-xs text-red-700">{issues.join(". ")}</p> : null;
}

export function Panel({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id={id} className="text-sm font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

const parse = (raw: string) => (raw.trim() === "" || !Number.isFinite(Number(raw)) ? undefined : Number(raw));
const show = (value: number | undefined) => (value === undefined ? "" : String(value));

/** A number input that lets a half-typed value sit in the box while committing each complete one. */
export function NumberField({
  label,
  value,
  onChange,
  step = "any",
  min,
  max,
  placeholder,
  issues = [],
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  step?: number | "any";
  min?: number;
  max?: number;
  placeholder?: string;
  issues?: string[];
}) {
  const issueId = useId();
  const [draft, setDraft] = useState(show(value));
  // Follow edits made elsewhere (a drag, undo) unless the box already says the same number.
  useEffect(() => setDraft((d) => (parse(d) === value ? d : show(value))), [value]);
  // The issue sits outside the label so it describes the input without joining its name.
  return (
    <div className="text-sm">
      <label className="block">
        <span className="font-medium text-zinc-700">{label}</span>
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          max={max}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            const n = parse(e.target.value);
            if (n !== undefined) onChange(n);
          }}
          onBlur={() => setDraft(show(value))}
          className={inputClass}
          {...invalidProps(issueId, issues)}
        />
      </label>
      <Issues id={issueId} issues={issues} />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
  issues = [],
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  issues?: string[];
}) {
  const issueId = useId();
  return (
    <div className="text-sm">
      <label className="block">
        <span className="font-medium text-zinc-700">{label}</span>
        <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} {...invalidProps(issueId, issues)}>
          {children}
        </select>
      </label>
      <Issues id={issueId} issues={issues} />
    </div>
  );
}

/** Keeps an unknown saved value selectable, so the select never silently shows another option. */
const extraOption = (value: string | undefined, known: readonly string[], label = `${value} (not found)`) =>
  value && !known.includes(value) ? <option value={value}>{label}</option> : null;

export function Palette({
  page,
  assets,
  readOnly = false,
  onAdd,
}: {
  page: Page;
  assets: TemplateAsset[];
  readOnly?: boolean;
  onAdd: (field: Field) => void;
}) {
  const [bind, setBind] = useState<string>("member.full_name");
  const [assetId, setAssetId] = useState("");
  const chosen = assets.find((a) => a.id === assetId) ?? assets[0];
  const add = (spec: NewField) => onAdd(newField(spec, page));
  return (
    <Panel title="Add a field">
      <fieldset disabled={readOnly} className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <SelectField label="Text bind" value={bind} onChange={setBind}>
              {TEXT_BINDS.map((b) => (
                <option key={b} value={b}>
                  {bindLabel(b)}
                </option>
              ))}
            </SelectField>
          </div>
          <ToolbarButton onClick={() => add({ kind: "text", bind })}>Add text</ToolbarButton>
        </div>
        <div className="flex flex-wrap gap-2">
          <ToolbarButton onClick={() => add({ kind: "qr" })}>Add QR code</ToolbarButton>
          <ToolbarButton onClick={() => add({ kind: "photo" })}>Add photo</ToolbarButton>
          <ToolbarButton onClick={() => add({ kind: "signature" })}>Add signature</ToolbarButton>
        </div>
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <SelectField label="Template image to add" value={chosen?.id ?? ""} onChange={setAssetId}>
              {assets.length === 0 ? <option value="">No images uploaded</option> : null}
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </SelectField>
          </div>
          <ToolbarButton disabled={!chosen} onClick={() => chosen && add({ kind: "image", asset: chosen })}>
            Add image
          </ToolbarButton>
        </div>
      </fieldset>
    </Panel>
  );
}

export function FieldInspector({
  index,
  field,
  page,
  fonts,
  assets,
  readOnly,
  dispatch,
  onRemove,
  issues,
}: {
  index: number;
  field: Field;
  page: Page;
  fonts: Font[];
  assets: TemplateAsset[];
  readOnly: boolean;
  dispatch: Dispatch<DesignerAction>;
  onRemove: () => void;
  /** Issues at a path, e.g. issues("fields[2].size_mm"). */
  issues: (path: string) => string[];
}) {
  const at = (key: string) => issues(`fields[${index}]${key ? `.${key}` : ""}`);
  const formatIssueId = useId();
  const slotIssueId = useId();
  const update = (patch: FieldPatch, key?: string) =>
    dispatch({ type: "update", index, patch, coalesce: key ? `field-${index}-${key}` : undefined });
  const number = (label: string, key: "x_mm" | "y_mm" | "w_mm" | "h_mm" | "size_mm" | "size_pt", placeholder?: string) => (
    <NumberField
      label={label}
      value={field[key]}
      placeholder={placeholder}
      issues={at(key)}
      onChange={(n) => update({ [key]: n }, key)}
    />
  );
  const families = [...new Set(fonts.map((f) => f.family))].sort();
  const uploaded = (style: string) => fonts.some((f) => f.family === field.font && f.style === style);

  return (
    <Panel title={fieldLabel(field, assets)}>
      <PanelIssues issues={[...at(""), ...at("type")]} />
      <fieldset disabled={readOnly} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {number("X (mm)", "x_mm")}
          {number("Y (mm)", "y_mm")}
        </div>
        {field.type === "text" || field.type === "qr" ? (
          <SelectField
            label="Bind"
            value={field.bind ?? ""}
            issues={at("bind")}
            onChange={(bind) => update({ bind, format: bind === "certificate.issued_at" ? field.format : undefined })}
          >
            {extraOption(field.bind, field.type === "qr" ? QR_BINDS : TEXT_BINDS)}
            {(field.type === "qr" ? QR_BINDS : TEXT_BINDS).map((b) => (
              <option key={b} value={b}>
                {bindLabel(b)}
              </option>
            ))}
          </SelectField>
        ) : null}
        {field.type === "text" ? (
          <>
            {field.bind === "certificate.issued_at" ? (
              <div className="text-sm">
                <label className="block">
                  <span className="font-medium text-zinc-700">Date format (Go layout)</span>
                  <input
                    value={field.format ?? ""}
                    placeholder="2006-01-02"
                    onChange={(e) => update({ format: e.target.value || undefined }, "format")}
                    className={inputClass}
                    {...invalidProps(formatIssueId, at("format"))}
                  />
                </label>
                <Issues id={formatIssueId} issues={at("format")} />
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <SelectField label="Font" value={field.font ?? ""} issues={at("font")} onChange={(font) => update({ font: font || undefined })}>
                <option value="">Helvetica (built in)</option>
                {extraOption(field.font, families, `${field.font} (not uploaded)`)}
                {families.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Style"
                value={field.font_style ?? "regular"}
                issues={at("font_style")}
                onChange={(style) => update({ font_style: style === "regular" ? undefined : (style as Field["font_style"]) })}
              >
                {FONT_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {STYLE_LABELS[s]}
                    {field.font && !uploaded(s) ? " (not uploaded)" : ""}
                  </option>
                ))}
              </SelectField>
              {number("Size (pt)", "size_pt", "14")}
              <SelectField label="Align" value={alignOf(field)} issues={at("align")} onChange={(align) => update({ align: align as Field["align"] })}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </SelectField>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={field.w_mm === undefined}
                onChange={(e) => update({ w_mm: e.target.checked ? undefined : textWidthMM(field, page) })}
              />
              <span className="text-zinc-700">Run to the right margin</span>
            </label>
            {field.w_mm !== undefined ? number("Width (mm)", "w_mm") : null}
          </>
        ) : null}
        {field.type === "qr" ? number("Size (mm)", "size_mm") : null}
        {field.type === "image" && field.asset_key ? (
          <SelectField
            label="Template image"
            value={field.asset_key}
            issues={at("asset_key")}
            onChange={(asset_key) => update({ asset_key })}
          >
            {extraOption(
              field.asset_key,
              assets.map((a) => a.id),
            )}
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </SelectField>
        ) : null}
        {field.type === "signature" ? (
          <div className="text-sm">
            <label className="block">
              <span className="font-medium text-zinc-700">Signature slot</span>
              <input
                value={field.slot ?? ""}
                placeholder="Signatory"
                maxLength={40}
                onChange={(e) => update({ slot: e.target.value || undefined }, "slot")}
                className={inputClass}
                {...invalidProps(slotIssueId, at("slot"))}
              />
            </label>
            <Issues id={slotIssueId} issues={at("slot")} />
            <p className="mt-1 text-xs text-zinc-500">Programs choose who signs each slot, e.g. Registrar or President.</p>
          </div>
        ) : null}
        {field.type === "image" || field.type === "signature" ? (
          <>
            {!field.asset_key ? <p className="text-xs text-zinc-500">Shows {field.bind}; previews draw a labelled box.</p> : null}
            <div className="grid grid-cols-2 gap-3">
              {number("Width (mm)", "w_mm")}
              {number("Height (mm)", "h_mm")}
            </div>
          </>
        ) : null}
      </fieldset>
      {readOnly ? null : (
        <ToolbarButton tone="danger" onClick={onRemove}>
          Remove field
        </ToolbarButton>
      )}
    </Panel>
  );
}

const PAGE_SIZES = ["A3", "A4", "A5", "Letter", "Legal"] as const;
const SIDES = ["top", "right", "bottom", "left"] as const;
const SIDE_LABELS = { top: "Top", right: "Right", bottom: "Bottom", left: "Left" };

export function PagePanel({
  page,
  readOnly,
  dispatch,
  issues,
}: {
  page: Page;
  readOnly: boolean;
  dispatch: Dispatch<DesignerAction>;
  issues: (path: string) => string[];
}) {
  const m = margins(page);
  return (
    <Panel title="Page">
      <PanelIssues issues={[...issues("page"), ...issues("page.margins_mm")]} />
      <fieldset disabled={readOnly} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Page size" value={page.size} issues={issues("page.size")} onChange={(size) => dispatch({ type: "page", patch: { size: size as Page["size"] } })}>
            {extraOption(page.size, PAGE_SIZES)}
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Orientation"
            value={page.orientation}
            issues={issues("page.orientation")}
            onChange={(orientation) => dispatch({ type: "page", patch: { orientation: orientation as Page["orientation"] } })}
          >
            {extraOption(page.orientation, ["portrait", "landscape"])}
            <option value="portrait">Portrait</option>
            <option value="landscape">Landscape</option>
          </SelectField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {SIDES.map((side) => (
            <NumberField
              key={side}
              label={`${SIDE_LABELS[side]} margin (mm)`}
              value={page.margins_mm?.[side]}
              min={0}
              placeholder="0"
              issues={issues(`page.margins_mm.${side}`)}
              onChange={(n) =>
                dispatch({ type: "page", patch: { margins_mm: { ...m, ...page.margins_mm, [side]: n } }, coalesce: `margin-${side}` })
              }
            />
          ))}
        </div>
      </fieldset>
    </Panel>
  );
}

export function LayersPanel({
  definition,
  assets,
  readOnly,
  dispatch,
  issues,
}: {
  definition: TemplateDefinition;
  assets: TemplateAsset[];
  readOnly: boolean;
  dispatch: Dispatch<DesignerAction>;
  issues: (path: string) => string[];
}) {
  const ids = assets.map((a) => a.id);
  const options = assets.map((a) => (
    <option key={a.id} value={a.id}>
      {a.name}
    </option>
  ));
  const watermark = definition.watermark;
  return (
    <Panel title="Background and watermark">
      <fieldset disabled={readOnly} className="space-y-3">
        <SelectField
          label="Background"
          value={definition.background?.asset_key ?? ""}
          issues={[...issues("background"), ...issues("background.asset_key")]}
          onChange={(key) => dispatch({ type: "background", asset_key: key || null })}
        >
          <option value="">None</option>
          {extraOption(definition.background?.asset_key, ids)}
          {options}
        </SelectField>
        <SelectField
          label="Watermark"
          value={watermark?.asset_key ?? ""}
          issues={[...issues("watermark"), ...issues("watermark.asset_key")]}
          onChange={(key) =>
            // A new watermark starts faint; the renderer requires an opacity.
            dispatch({ type: "watermark", value: key ? { asset_key: key, opacity: watermark?.opacity ?? 0.1 } : null })
          }
        >
          <option value="">None</option>
          {extraOption(watermark?.asset_key, ids)}
          {options}
        </SelectField>
        {watermark ? (
          <NumberField
            label="Opacity (0 to 1)"
            value={watermark.opacity}
            step={0.05}
            min={0}
            max={1}
            issues={issues("watermark.opacity")}
            onChange={(opacity) => dispatch({ type: "watermark", value: { ...watermark, opacity }, coalesce: "opacity" })}
          />
        ) : null}
        <p className="text-xs text-zinc-500">
          The background fills the whole page. The watermark is centred inside the margins and scaled to fit.
        </p>
      </fieldset>
    </Panel>
  );
}
